import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { isEmpty } from "lodash";
import { getICReport, ICReportSlice } from "services/icDashboard/icDashboard.service";
import { customAsyncThunk } from "store/slice/slices.functions";
import { ICReportConverted } from "./icDashboard.types";
import { convertICReportData } from "./icReport.converter";

const initialState: ICReportSlice = {
    data: null,
    error: null,
    status: "pristine",
    loadedParams: null,
};

// Thunk
export const fetchICReport = customAsyncThunk<
    ICReportConverted | null,
    { projectId: string | null; insightId: string | null }
>("leadershipDashboard/fetchIcReport", async ({ projectId, insightId }) => {
    const { data } = await getICReport(projectId, insightId);

    if (isEmpty(data)) {
        return null;
    }

    return convertICReportData(data);
});

// Slice
const icDashboardSlice = createSlice({
    name: "project/icDashboard",
    initialState,
    reducers: {
        clearICDashboard: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchICReport.pending, (state, action) => {
                state.status = "fetching";

                const { projectId, insightId } = action.meta.arg;
                const isSameAsLoaded =
                    state.loadedParams?.projectId === projectId && state.loadedParams?.insightId === insightId;

                if (!isSameAsLoaded) {
                    state.data = null;
                }
            })
            .addCase(fetchICReport.fulfilled, (state, action) => {
                state.status = "done";
                state.data = action.payload;
                state.loadedParams = action.meta.arg;
            })
            .addCase(fetchICReport.rejected, (state) => {
                state.status = "error";
            });
    },
});

// Actions
export const { clearICDashboard } = icDashboardSlice.actions;

// Selectors
export const getIcDashboard = (store: RootState): ICReportSlice => store.project.icDashboard;

export default icDashboardSlice.reducer;
